import { useState, useRef, useCallback, useEffect } from 'react'

/**
 * Custom hook for resilient auto-saving with exponential backoff retry
 * 
 * @param {Function} saveFunction - Async function to call when auto-saving. Should return a promise.
 * @param {Function} hasStateChanged - Function that returns true if state has changed since last save
 * @param {Object} options - Configuration options
 * @param {number} options.initialDelay - Initial delay in milliseconds before auto-saving (default: 1500)
 * @param {number} options.initialRetryDelay - Initial retry delay in milliseconds (default: 30000 = 30 seconds)
 * @param {number} options.maxRetryDelay - Maximum retry delay in milliseconds (default: 300000 = 5 minutes)
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: Infinity, set to number to limit)
 * @param {boolean} options.enabled - Whether auto-save is enabled (default: true)
 * @param {Function} options.onSaveSuccess - Callback called after successful save
 * @param {Function} options.onSaveError - Callback called after failed save
 * @param {Function} options.onRetry - Callback called before each retry attempt
 * @param {boolean} options.resetOnSuccess - Whether to reset retry state on successful save (default: true)
 * 
 * @returns {Object} - Object containing auto-save state and control functions
 * @returns {boolean} isAutoSaving - Whether auto-save is currently in progress
 * @returns {Object} autoSaveStatus - Status object with { text: string, status: 'idle' | 'saving' | 'saved' | 'error' | 'retrying' }
 * @returns {number} retryAttempt - Current retry attempt number (0 = no retry)
 * @returns {number} nextRetryDelay - Delay for next retry in milliseconds
 * @returns {Function} triggerAutoSave - Manually trigger auto-save
 * @returns {Function} cancelRetry - Cancel pending retry
 * @returns {Function} resetAutoSave - Reset auto-save state
 */
export const useResilientAutoSave = (saveFunction, hasStateChanged, options = {}) => {
  const {
    initialDelay = 1500,
    initialRetryDelay = 30000, // 30 seconds
    maxRetryDelay = 300000, // 5 minutes
    backoffMultiplier = 2,
    maxRetries = Infinity,
    enabled = true,
    onSaveSuccess,
    onSaveError,
    onRetry,
    resetOnSuccess = true,
  } = options

  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState({ text: '', status: 'idle' })
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [nextRetryDelay, setNextRetryDelay] = useState(initialRetryDelay)

  // Refs to track state and prevent race conditions
  const debounceTimeoutRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  const isMountedRef = useRef(true)
  const isSavingRef = useRef(false)
  const currentRetryAttemptRef = useRef(0)
  const lastSavePromiseRef = useRef(null)

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Calculate next retry delay with exponential backoff
   */
  const calculateNextRetryDelay = useCallback((attempt) => {
    const delay = initialRetryDelay * Math.pow(backoffMultiplier, attempt)
    return Math.min(delay, maxRetryDelay)
  }, [initialRetryDelay, backoffMultiplier, maxRetryDelay])

  /**
   * Reset retry state
   */
  const resetRetryState = useCallback(() => {
    currentRetryAttemptRef.current = 0
    setRetryAttempt(0)
    setNextRetryDelay(initialRetryDelay)
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }, [initialRetryDelay])

  /**
   * Schedule next retry
   */
  const scheduleRetry = useCallback((attempt) => {
    if (!isMountedRef.current) return

    // Check if max retries reached
    if (attempt >= maxRetries) {
      setAutoSaveStatus({ 
        text: `Save failed after ${attempt} attempts`, 
        status: 'error' 
      })
      if (onSaveError) {
        onSaveError(new Error(`Max retries (${maxRetries}) reached`), attempt)
      }
      return
    }

    const delay = calculateNextRetryDelay(attempt)
    setNextRetryDelay(delay)
    
    if (onRetry) {
      onRetry(attempt + 1, delay)
    }

    retryTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return
      retryTimeoutRef.current = null
      performAutoSave(true, attempt + 1)
    }, delay)
  }, [calculateNextRetryDelay, maxRetries, onRetry, onSaveError])

  /**
   * Perform the actual auto-save
   */
  const performAutoSave = useCallback(async (isRetry = false, attempt = 0) => {
    // Don't save if disabled
    if (!enabled || !saveFunction) {
      return
    }

    // Don't save if already saving (unless it's a retry of a failed save)
    if (isSavingRef.current && !isRetry) {
      return
    }

    // Don't save if no changes detected (unless it's a retry)
    if (!isRetry && !hasStateChanged()) {
      return
    }

    // Set saving state
    isSavingRef.current = true
    setIsAutoSaving(true)
    currentRetryAttemptRef.current = attempt
    setRetryAttempt(attempt)

    if (isRetry) {
      setAutoSaveStatus({ text: `Retrying save (attempt ${attempt + 1})...`, status: 'retrying' })
    } else {
      setAutoSaveStatus({ text: 'Saving...', status: 'saving' })
    }

    try {
      // Create and track the save promise
      const savePromise = saveFunction()
      lastSavePromiseRef.current = savePromise
      
      await savePromise

      // Check if this is still the latest save attempt
      if (currentRetryAttemptRef.current !== attempt && !isRetry) {
        // A newer save was triggered, ignore this result
        return
      }

      // Success!
      if (!isMountedRef.current) return

      setAutoSaveStatus({ text: 'Saved', status: 'saved' })
      
      // Reset retry state on success
      if (resetOnSuccess) {
        resetRetryState()
      }

      // Call success callback
      if (onSaveSuccess) {
        onSaveSuccess(attempt)
      }

      // Clear status after 3 seconds
      setTimeout(() => {
        if (isMountedRef.current) {
          setAutoSaveStatus(prev => {
            // Only clear if still showing 'saved' status (user might have made new changes)
            if (prev.status === 'saved') {
              return { text: '', status: 'idle' }
            }
            return prev
          })
        }
      }, 3000)

    } catch (error) {
      // Check if this is still the latest save attempt
      if (currentRetryAttemptRef.current !== attempt && !isRetry) {
        // A newer save was triggered, ignore this error
        return
      }

      if (!isMountedRef.current) return

      console.error('Auto-save failed:', error)
      const statusText = error?.autoSaveStatusText ||
        (attempt === 0 ? 'Save failed' : `Save failed (attempt ${attempt + 1})`)
      setAutoSaveStatus({ 
        text: statusText,
        status: 'error' 
      })

      // Call error callback
      if (onSaveError) {
        onSaveError(error, attempt)
      }

      // Schedule retry if enabled and not at max retries
      if (!error?.skipRetry && attempt < maxRetries) {
        scheduleRetry(attempt)
      } else {
        // Max retries reached, stop trying
        setAutoSaveStatus({ 
          text: error?.autoSaveStatusText || `Save failed after ${attempt + 1} attempts`,
          status: 'error' 
        })
      }
    } finally {
      // Only reset saving state if this is still the current attempt
      if (currentRetryAttemptRef.current === attempt || isRetry) {
        isSavingRef.current = false
        setIsAutoSaving(false)
      }
    }
  }, [
    enabled,
    saveFunction,
    hasStateChanged,
    resetOnSuccess,
    resetRetryState,
    scheduleRetry,
    maxRetries,
    onSaveSuccess,
    onSaveError,
  ])

  /**
   * Trigger auto-save with debounce
   */
  const triggerAutoSave = useCallback((immediate = false) => {
    if (!enabled || !saveFunction) {
      return
    }

    // Clear existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
      debounceTimeoutRef.current = null
    }

    // If immediate, save right away
    if (immediate) {
      performAutoSave(false, 0)
      return
    }

    // Otherwise, debounce
    debounceTimeoutRef.current = setTimeout(() => {
      debounceTimeoutRef.current = null
      performAutoSave(false, 0)
    }, initialDelay)
  }, [enabled, saveFunction, initialDelay, performAutoSave])

  /**
   * Cancel pending retry
   */
  const cancelRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    resetRetryState()
  }, [resetRetryState])

  /**
   * Reset auto-save state
   */
  const resetAutoSave = useCallback(() => {
    // Clear debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
      debounceTimeoutRef.current = null
    }

    // Cancel retry
    cancelRetry()

    // Reset state
    setIsAutoSaving(false)
    setAutoSaveStatus({ text: '', status: 'idle' })
    isSavingRef.current = false
  }, [cancelRetry])

  /**
   * Force immediate save (bypasses debounce and retry logic)
   */
  const forceSave = useCallback(async () => {
    // Cancel any pending operations
    resetAutoSave()
    
    // Perform save immediately
    await performAutoSave(false, 0)
  }, [resetAutoSave, performAutoSave])

  return {
    isAutoSaving,
    autoSaveStatus,
    retryAttempt,
    nextRetryDelay,
    triggerAutoSave,
    cancelRetry,
    resetAutoSave,
    forceSave,
  }
}

export default useResilientAutoSave
